import * as React from 'react'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// constants
import { SELECT_GROUP_LABELS } from 'constants/forms'
import { RECIPIENT_TYPE_OPTIONS } from 'constants/systemProcesses'
// graphql
import { USERS_OR_POSITIONS_QUERY } from 'graphql/user/queries'
// components
import { CustomOption } from './components/Option'
import { ReduxEntitySelect } from 'components/FormElements'
// types
import { IOption } from 'components/FormElements/Select'
import { FieldType } from 'types/graphql'
// constants
import { ENTITIES } from 'constants/entities'
// views
import { FieldWrapper } from './view'
// utils
import get from 'lodash/get'
import uniqBy from 'lodash/uniqBy'
import { required } from 'utils/index'
import { getMemberOrPositionLabel, getTemplateFieldsOptions } from 'utils/systemProcess'

interface Props {
  field: string
  riskTemplateFields: (IOption & any)[]
  parentRiskTemplatesFields: (IOption & any)[]
}

const RecipientSelect: React.FC<Props> = ({ field, riskTemplateFields, parentRiskTemplatesFields }) => {
  const { isRiskTemplate, riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const getOptions = (res) => {
    const options = get(res, 'data.usersOrPositions.data', []).map((item) => ({
      ...item,
      label: getMemberOrPositionLabel(item),
      value: item.id,
      entity: Object.hasOwn(item, 'userId') ? ENTITIES.POSITION : ENTITIES.USER
    }))

    return [
      {
        label: 'Persons',
        options: options
      }
    ]
  }

  const prepareOptions = (options: IOption[]) => {
    const uniqFieldLinks = uniqBy(riskTemplate?.fieldLinks, (item) => `${item.field.entityId}-${item.field.id}`)

    const fieldLinks = uniqFieldLinks
      .filter((item) => item.field.fieldType === FieldType.Lookup)
      .map(({ field }) => getTemplateFieldsOptions({ field }))
      .flat()

    return [
      ...(isRiskTemplate
        ? [
            {
              label: SELECT_GROUP_LABELS.FIELDS,
              options: riskTemplateFields
            },
            { label: SELECT_GROUP_LABELS.ANOTHER_FORM_FIELDS, options: fieldLinks },
            ...parentRiskTemplatesFields
              .filter((item) => item.fields.length)
              .map((item) => ({
                label: item.name,
                options: item.fields
              }))
          ]
        : [{ label: SELECT_GROUP_LABELS.ROLES, options: RECIPIENT_TYPE_OPTIONS }]),
      ...options
    ]
  }

  const handleSearch = ({ search, refetch }) => refetch({ filter: { search } })

  const isOptionSelected = (option: IOption, selected: IOption & any = []) => {
    return option.value === selected[0]?.value && option.label === selected[0]?.label.trim()
  }

  return (
    <FieldWrapper>
      <ReduxEntitySelect
        validate={[required]}
        getOptions={getOptions}
        name={`${field}.recipient`}
        handleSearch={handleSearch}
        prepareOptions={prepareOptions}
        query={USERS_OR_POSITIONS_QUERY}
        components={{ Option: CustomOption }}
        isOptionSelected={isOptionSelected}
      />
    </FieldWrapper>
  )
}

export default RecipientSelect
